// Register.js — Student registration page
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

const DEPARTMENTS = ['AI&DS', 'CSE', 'ECE', 'IT', 'EEE', 'MECH', 'CIVIL'];

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    department: '', cgpa: '', arrears: '', skills: ''
  });
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = e =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name || !form.email || !form.password || !form.department)
      return 'Name, email, password and department are required.';
    if (form.password !== form.confirmPassword)
      return 'Passwords do not match.';
    if (form.password.length < 6)
      return 'Password must be at least 6 characters.';
    const cgpa = parseFloat(form.cgpa);
    if (isNaN(cgpa) || cgpa < 0 || cgpa > 10)
      return 'CGPA must be between 0 and 10.';
    const arrears = parseInt(form.arrears, 10);
    if (isNaN(arrears) || arrears < 0)
      return 'Arrears must be 0 or a positive number.';
    return null;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setSuccess('');
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    try {
      await API.post('/register', {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        department: form.department,
        cgpa: parseFloat(form.cgpa),
        arrears: parseInt(form.arrears || '0', 10),
        skills: form.skills.trim()
      });
      setSuccess('Registration successful! Redirecting to login…');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Email may already be in use.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{ padding: '2rem 1rem' }}>
      <div className="auth-box" style={{ maxWidth: 540 }}>
        {/* Header */}
        <div className="auth-header">
          <span className="auth-logo">📝</span>
          <h1>Create Account</h1>
          <p>Join the placement portal and discover opportunities</p>
        </div>

        {error   && <div className="alert alert-error">⚠️ {error}</div>}
        {success && <div className="alert alert-success">✅ {success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Name + Email */}
          <div className="form-row">
            <div className="form-group">
              <label>Full Name</label>
              <input name="name" placeholder="Niranjan R" value={form.name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" placeholder="you@college.edu" value={form.email} onChange={handleChange} />
            </div>
          </div>

          {/* Password + Confirm */}
          <div className="form-row">
            <div className="form-group">
              <label>Password</label>
              <input type="password" name="password" placeholder="Min 6 chars" value={form.password} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input type="password" name="confirmPassword" placeholder="Repeat password" value={form.confirmPassword} onChange={handleChange} />
            </div>
          </div>

          {/* Department */}
          <div className="form-group">
            <label>Department</label>
            <select name="department" value={form.department} onChange={handleChange}>
              <option value="">-- Select Department --</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* CGPA + Arrears */}
          <div className="form-row">
            <div className="form-group">
              <label>CGPA (0 – 10)</label>
              <input
                type="number" name="cgpa" placeholder="e.g. 7.6"
                min="0" max="10" step="0.01"
                value={form.cgpa} onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Active Arrears</label>
              <input
                type="number" name="arrears" placeholder="e.g. 0"
                min="0" value={form.arrears} onChange={handleChange}
              />
            </div>
          </div>

          {/* Skills */}
          <div className="form-group">
            <label>Skills (comma-separated)</label>
            <input name="skills" placeholder="Python, React, SQL, Machine Learning…" value={form.skills} onChange={handleChange} />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg btn-full"
            disabled={loading}
          >
            {loading ? 'Creating account…' : 'Create Account →'}
          </button>
        </form>

        <div className="auth-footer">
          Already have an account?{' '}
          <span
            style={{ color: 'var(--accent-blue)', cursor: 'pointer', fontWeight: 600 }}
            onClick={() => navigate('/login')}
          >
            Sign in
          </span>
        </div>
      </div>
    </div>
  );
}
